/* Per-record links for the finance screens (2026-10-27): /admin/weekly-sale/383ui-aj392 opens that
 * record's normal edit / approve form. The screen is wrapped with helper/withRouter.js, so it has
 * props.params.publicId and props.navigate. Each screen keeps `this.openPid` = the public id of the
 * record its form is showing (null on the list), and calls these at four moments:
 *
 *   recordOpened(this, BASE, pid)        it opened a record from the list, or saved a new one
 *   recordClosed(this, BASE)             the form closed (Exit / Cancel / after delete or approve)
 *   recordParamChanged(this, prev, open, close)   from componentDidUpdate: Back / Forward
 *   recordNotFound(this, BASE)           a link that does not exist, or is not this user's
 *
 * The lookup itself is server-side and scoped by the signed-in user (helper/PublicId.php), so a link
 * only ever opens what that user could open from their own list.
 */

// Events between a screen and the admin layout's "No access" page (2026-10-29).
export const NO_ACCESS_EVENT = 'pc:no-access';
export const NO_ACCESS_RESET_EVENT = 'pc:no-access-reset';

/* The form now shows record `pid`: put its link in the address bar (a history entry, so Back
 * returns to the list). `replace` for a just-saved record, whose "add" step needs no entry. */
export function recordOpened(cmp, base, pid, { replace = false } = {}) {
    if (!pid || !cmp.props.navigate) return;
    cmp.openPid = pid;
    if (cmp.props.params?.publicId !== pid) cmp.props.navigate(`${base}/${pid}`, { replace });
}

/* The form closed: back to the list's own address, if a record's link is showing. */
export function recordClosed(cmp, base) {
    cmp.openPid = null;
    if (cmp.props.navigate && cmp.props.params?.publicId) cmp.props.navigate(base);
}

/* Back / Forward between the list and a record. `open(pid)` loads a record, `close()` returns the
 * screen to its list (without navigating again). Changes the screen made itself are ignored. */
export function recordParamChanged(cmp, prevProps, open, close) {
    const before = prevProps.params?.publicId;
    const now = cmp.props.params?.publicId;
    if (before === now) return;
    // A different record (or the list) is in the address now: drop any "No access" shown for the
    // previous link. If this one is refused too, recordNotFound() puts it back.
    window.dispatchEvent(new CustomEvent(NO_ACCESS_RESET_EVENT));
    if (now && now !== cmp.openPid) open(now);
    else if (!now && cmp.openPid) { cmp.openPid = null; close(); }
}

/* A link the server would not open (another shop's / warehouse's / account's record, a screen the
 * role has no right to, or no such record): the page shows the same "No access" as the screen
 * guard (user, 2026-10-29). The SAME answer for "not yours" and "doesn't exist", so a link reveals
 * nothing about records the user cannot see. Layout/AdminLayout listens for this event. `base` is
 * kept so the screens' calls don't change. */
export async function recordNotFound(cmp, base) { // eslint-disable-line no-unused-vars
    cmp.openPid = null;
    window.dispatchEvent(new CustomEvent(NO_ACCESS_EVENT, { detail: { path: window.location.pathname } }));
}
